import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  selectAuthLoading,
  selectIsAuthenticated,
  checkAuthSuccess, 
  checkAuthFailure,
  User
} from '../../store/slices/authSlice';
import { authService } from '../../services/authService';
import type { AuthCheckResponse } from '../../services/authService';

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectAuthLoading);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Development mode bypass
  const isDevelopment = process.env.NODE_ENV === 'development';

  useEffect(() => {
    const token = authService.getToken();
    
    // Development mode - skip auth checks if no token
    if (isDevelopment && !token) {
      console.log('Development mode: No token found, skipping auth check');
      dispatch(checkAuthFailure());
      return;
    }
    
    // Development mode quick bypass
    if (isDevelopment && window.location.search.includes('skip-auth')) {
      dispatch(checkAuthFailure());
      return;
    }

    // If no token, don't show loading - immediately fail auth check
    if (!token) {
      dispatch(checkAuthFailure());
      return;
    }

    // Only initialize if there's a token and we're not already authenticated
    if (token && !isAuthenticated) {
      const initializeAuth = async () => {
        try {
          
          const response = await authService.checkAuth();
          
          // Check if response has the expected structure
          if (response && response.success && response.data && response.data.user) {
            // Standard Laravel API response format
            dispatch(checkAuthSuccess({
              user: response.data.user,
              permissions: response.data.permissions || [],
              roles: response.data.roles || [response.data.user?.role || 'user']
            }));
          } else if (response && response.data && response.data.user) {
            // Alternative response structure
            dispatch(checkAuthSuccess({
              user: response.data.user,
              permissions: response.data.permissions || [],
              roles: response.data.roles || [response.data.user?.role || 'user']
            }));
          } else if (response && (response as AuthCheckResponse).user) {
            // Direct user object in response
            const authResponse = response as AuthCheckResponse;
            dispatch(checkAuthSuccess({
              user: authResponse.user!,
              permissions: authResponse.permissions || [],
              roles: authResponse.roles || [authResponse.user?.role || 'user']
            }));
          } else if (response && (response as AuthCheckResponse).id && (response as AuthCheckResponse).name) {
            // Response is the user object itself
            const userResponse = response as AuthCheckResponse;
            dispatch(checkAuthSuccess({
              user: {
                id: userResponse.id!,
                name: userResponse.name!,
                email: userResponse.email || '',
                role: userResponse.role || 'user',
                is_active: userResponse.is_active || true,
                employee_id: '',
                permissions: [],
                created_at: '',
                updated_at: ''
              } as User,
              permissions: [],
              roles: [userResponse.role || 'user']
            }));
          } else {
            console.warn('Unexpected auth response format:', response);
            throw new Error('Invalid response from auth service');
          }
        } catch (error) {
          // On error, clear invalid token and fail auth
          console.warn('Auth check failed:', error);
          authService.setToken(''); // Clear token using service method
          dispatch(checkAuthFailure());
        }
      };

      initializeAuth();
    }
    
    // Return empty cleanup function for cases that don't need cleanup
    return () => {};
  }, [dispatch, isAuthenticated, isDevelopment]);

  // Show loading screen during initial auth check
  if (isLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="flex flex-col items-center space-y-3">
          <div className="relative">
            <div className="h-12 w-12 rounded-full border-4 border-blue-100"></div>
            <div className="absolute top-0 left-0 h-12 w-12 rounded-full border-4 border-transparent border-t-primary-600 animate-spin"></div>
          </div>
          <p className="text-sm font-medium text-gray-500">Memverifikasi sesi...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthProvider;